import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Eye, Target, Sparkles, ArrowRight, Plus, Minus, BadgeCheck, Quote, Download } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import { aboutIntro, missionVision, whyChoose, faqs, stats, founder } from "../mock";

export default function About() {
  const [open, setOpen] = useState(0);

  return (
    <div>
      <PageHeader
        title="About Us"
        subtitle="Connect with professionals to gain peace of mind in making the right decision for your business."
      />

      {/* Who we are */}
      <section className="py-20 md:py-24">
        <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="rounded-2xl overflow-hidden shadow-[0_30px_60px_-30px_rgba(10,26,52,.5)]">
              <img src={aboutIntro.image} alt="BEE Exsellance team" className="w-full h-[440px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="eyebrow mb-4">About Who We Are</div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-[#0B1F3A] leading-tight">
              Gain Peace of Mind in Making the Right Decision
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Choosing a competent consultant is arguably the most crucial decision for your business.
              Consider academic qualifications, professionalism, years of experience, breadth of knowledge,
              and client endorsements — valuable advice can propel your business to new heights.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              At BEE Exsellance we take our business practices seriously. Our services cover all aspects of
              B-BBEE across various sectors, delivered by a dedicated team of experts alongside specialised associates.
            </p>
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/contact" className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-lg">
                Talk to an Expert <ArrowRight size={17} />
              </Link>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/company-profile/download`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-slate-200 text-[#0B1F3A] font-semibold hover:border-[#D4AF37] transition-colors"
              >
                <Download size={17} /> Download Company Profile
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Stats band */}
      <section className="bg-[#0B1F3A] py-14">
        <div className="container-x grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-head text-4xl md:text-5xl font-extrabold text-white">
                {s.value}<span className="text-[#D4AF37]">{s.suffix}</span>
              </div>
              <div className="divider-gold mx-auto my-3" />
              <div className="text-sm text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Meet the Founder */}
      <section className="py-20 md:py-24">
        <div className="container-x">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <div className="eyebrow justify-center mb-4" style={{ display: "inline-flex" }}>Meet the Founder</div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-[#0B1F3A] leading-tight">
              A decade of B-BBEE, auditing &amp; finance expertise behind you
            </h2>
          </Reveal>

          <div className="grid lg:grid-cols-5 gap-10 items-start">
            {/* Photo */}
            <Reveal className="lg:col-span-2">
              <div className="relative">
                <div className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-[#184E9E]/15 to-[#D4AF37]/20 -z-10" />
                <div className="rounded-2xl overflow-hidden shadow-[0_30px_60px_-30px_rgba(11,31,58,.55)]">
                  <img src={founder.photo} alt={founder.name} className="w-full h-[440px] object-cover object-center bg-white" />
                </div>
                <div className="absolute -bottom-5 left-5 right-5 bg-[#0B1F3A] text-white rounded-xl px-5 py-4 shadow-xl flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37] grid place-items-center text-[#0B1F3A] shrink-0">
                    <BadgeCheck size={22} />
                  </div>
                  <div>
                    <div className="font-head font-bold leading-tight">{founder.name}</div>
                    <div className="text-[13px] text-[#D4AF37]">{founder.title}</div>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Bio */}
            <Reveal delay={120} className="lg:col-span-3">
              <Quote size={34} className="text-[#D4AF37] mb-3" />
              {founder.bio.map((p, i) => (
                <p key={i} className="text-slate-600 leading-relaxed mb-4">{p}</p>
              ))}

              <div className="flex flex-wrap gap-2.5 mt-5">
                {founder.expertise.map((e) => (
                  <span key={e} className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-3 py-1.5 rounded-full bg-[#F6F7F9] text-[#184E9E] border border-slate-100">
                    <BadgeCheck size={14} className="text-[#D4AF37]" /> {e}
                  </span>
                ))}
              </div>

              <Link to="/contact" className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-lg mt-8">
                Work With Us <ArrowRight size={17} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>


      {/* Mission / Vision / Values */}
      <section className="py-20 md:py-24">
        <div className="container-x">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <div className="eyebrow justify-center mb-4" style={{ display: "inline-flex" }}>A Company On A Mission</div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-[#0B1F3A] leading-tight">
              Fulfilling the Individual Needs & Desires of Our Clients
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            <Reveal>
              <div className="card-lift bg-white rounded-2xl p-8 border border-slate-100 h-full">
                <div className="w-14 h-14 rounded-xl bg-[#F6F7F9] grid place-items-center text-[#184E9E] mb-5"><Eye size={26} /></div>
                <h3 className="font-head font-semibold text-xl text-[#0B1F3A] mb-3">Our Vision</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{missionVision.vision}</p>
              </div>
            </Reveal>
            <Reveal delay={90}>
              <div className="card-lift bg-[#0B1F3A] rounded-2xl p-8 h-full">
                <div className="w-14 h-14 rounded-xl bg-white/10 grid place-items-center text-[#D4AF37] mb-5"><Target size={26} /></div>
                <h3 className="font-head font-semibold text-xl text-white mb-3">Our Mission</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{missionVision.mission}</p>
              </div>
            </Reveal>
            <Reveal delay={180}>
              <div className="card-lift bg-white rounded-2xl p-8 border border-slate-100 h-full">
                <div className="w-14 h-14 rounded-xl bg-[#F6F7F9] grid place-items-center text-[#184E9E] mb-5"><Sparkles size={26} /></div>
                <h3 className="font-head font-semibold text-xl text-[#0B1F3A] mb-3">Core Values</h3>
                <ul className="space-y-2.5">
                  {missionVision.values.map((v) => (
                    <li key={v} className="flex items-center gap-2.5 text-[15px] text-slate-700">
                      <CheckCircle2 size={19} className="text-[#184E9E]" /> {v}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="py-20 md:py-24 bg-[#F6F7F9]">
        <div className="container-x max-w-3xl">
          <Reveal>
            <div className="eyebrow mb-4">{whyChoose.eyebrow}</div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-[#0B1F3A] leading-tight">{whyChoose.title}</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{whyChoose.body}</p>
            <ul className="mt-6 space-y-3">
              {whyChoose.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-[15px] text-slate-700">
                  <CheckCircle2 size={20} className="text-[#184E9E] shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 md:py-24">
        <div className="container-x max-w-3xl">
          <Reveal className="text-center mb-12">
            <div className="eyebrow justify-center mb-4" style={{ display: "inline-flex" }}>FAQs</div>
            <h2 className="font-head text-3xl md:text-4xl font-bold text-[#0B1F3A]">Answers to Frequently Asked Questions</h2>
          </Reveal>
          <div className="space-y-3">
            {faqs.map((f, idx) => (
              <div key={idx} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <button
                  onClick={() => setOpen(open === idx ? -1 : idx)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
                >
                  <span className="font-head font-semibold text-[#0B1F3A] text-[16px]">{f.q}</span>
                  <span className="w-8 h-8 rounded-full bg-[#F6F7F9] grid place-items-center text-[#184E9E] shrink-0">
                    {open === idx ? <Minus size={17} /> : <Plus size={17} />}
                  </span>
                </button>
                <div
                  className="grid transition-all duration-300 ease-in-out"
                  style={{ gridTemplateRows: open === idx ? "1fr" : "0fr" }}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 pb-5 text-sm text-slate-600 leading-relaxed">{f.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
